import React, {
  createContext,
  PropsWithChildren,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useState,
} from "react";
import { Table } from "@tanstack/react-table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tracify/ui/select";
import { Input } from "@tracify/ui/input";
import {
  Pagination,
  PaginationButton,
  PaginationEllipsis,
  PaginationNextButton,
  PaginationPreviousButton,
  PaginationStartButton,
  PaginationEndButton,
} from "@tracify/ui/pagination";
import { PaginationItem } from "@tracify/ui/pagination";
import { PaginationContent } from "@tracify/ui/pagination";

export function TablePagination<T extends Record<string, unknown>>({
  table,
  disableRowsPerPage,
  tableRef,
  children,
  rowsPerPageOptions = [10, 25, 50, 100, 200],
  variant = "alternate",
}: PropsWithChildren<{
  table: Table<T>;
  disableRowsPerPage?: boolean;
  rowsPerPageOptions?: number[];
  variant?: "default" | "alternate";
  tableRef: React.MutableRefObject<HTMLDivElement | null>;
}>): ReactElement | null {
  const pageIndex = table.getState().pagination.pageIndex;
  const pageCount = table.getPageCount();

  const handleChangePage = useCallback(
    (newPage: number) => {
      table.setPagination((curr) => ({
        ...curr,
        pageIndex: newPage,
      }));
      tableRef.current?.scrollTo?.({ top: 0, left: 0 });
    },
    [table, tableRef]
  );
  const onChangeRowsPerPage = useCallback(
    (value: string) => {
      table.setPageSize(Number(value));
    },
    [table]
  );

  const renderPageButtonsAndEllipses = useCallback(() => {
    const contentItems: React.ReactNode[] = [];
    // Start Ellipsis: Show if the "previous page button" would skip page 0.
    // This happens if pageIndex > 1 (current page is 0-indexed 2 or higher).
    if (pageIndex > 1) {
      contentItems.push(
        <PaginationItem key="start-ellipsis">
          <PaginationEllipsis />
        </PaginationItem>
      );
    }

    // Previous page button (actual number, not arrow)
    if (pageIndex - 1 > 0) {
      contentItems.push(
        <PaginationItem key={`page-${pageIndex - 2}`}>
          <PaginationButton
            onClick={() => handleChangePage(pageIndex - 2)}
            isActive={false}
          >
            {pageIndex - 1 /* Display is 1-based: (pageIndex - 1) + 1 */}
          </PaginationButton>
        </PaginationItem>
      );
    }

    // Previous page button (actual number, not arrow)
    if (pageIndex > 0) {
      contentItems.push(
        <PaginationItem key={`page-${pageIndex - 1}`}>
          <PaginationButton
            onClick={() => handleChangePage(pageIndex - 1)}
            isActive={false}
          >
            {pageIndex /* Display is 1-based: (pageIndex - 1) + 1 */}
          </PaginationButton>
        </PaginationItem>
      );
    }

    // Current page button
    contentItems.push(
      <PaginationItem key={`page-${pageIndex}`}>
        <PaginationButton
          onClick={() => handleChangePage(pageIndex)}
          isActive={true}
          variant={variant === "default" ? "muted" : "outline"}
        >
          {pageIndex + 1}
        </PaginationButton>
      </PaginationItem>
    );

    // Next page button (actual number, not arrow)
    if (pageIndex < pageCount - 1) {
      contentItems.push(
        <PaginationItem key={`page-${pageIndex + 1}`}>
          <PaginationButton
            onClick={() => handleChangePage(pageIndex + 1)}
            isActive={false}
          >
            {pageIndex + 2 /* Display is 1-based: (pageIndex + 1) + 1 */}
          </PaginationButton>
        </PaginationItem>
      );
    }

    if (pageIndex < pageCount - 2) {
      contentItems.push(
        <PaginationItem key={`page-${pageIndex + 2}`}>
          <PaginationButton
            onClick={() => handleChangePage(pageIndex + 2)}
            isActive={false}
          >
            {pageIndex + 3 /* Display is 1-based: (pageIndex + 1) + 1 */}
          </PaginationButton>
        </PaginationItem>
      );
    }

    // End Ellipsis: Show if the "next page button" doesn't reach the last page.
    // This happens if pageIndex < pageCount - 2 (current is at least two pages before the end).
    if (pageIndex < pageCount - 3) {
      contentItems.push(
        <PaginationItem key="end-ellipsis">
          <PaginationEllipsis />
        </PaginationItem>
      );
    }
    return contentItems;
  }, [handleChangePage, pageCount, pageIndex, variant]);

  return (
    <div className="flex flex-col-reverse lg:flex-row items-center gap-2">
      {!disableRowsPerPage && (
        <div className="flex items-center shrink-0">
          <p className="text-sm mr-2">Rows per Page:</p>
          <Select
            value={table.getState().pagination.pageSize.toString()}
            onValueChange={onChangeRowsPerPage}
          >
            <SelectTrigger variant={variant} size="sm" className="w-20">
              <SelectValue placeholder="Select rows" />
            </SelectTrigger>
            <SelectContent>
              {rowsPerPageOptions.map((option) => (
                <SelectItem key={option} value={option.toString()}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {pageCount > 1 ? (
        <TablePaginationProvider table={table} onPageChange={handleChangePage}>
          <div className="flex flex-col lg:flex-row items-center gap-2">
            <Pagination>
              <PaginationContent>
                <PaginationItem className="hidden lg:block">
                  <PaginationStartButton
                    onClick={() => handleChangePage(0)}
                    disabled={pageIndex === 0}
                  />
                </PaginationItem>
                <PaginationItem>
                  <PaginationPreviousButton
                    onClick={() => handleChangePage(pageIndex - 1)}
                    disabled={!table.getCanPreviousPage()}
                  />
                </PaginationItem>
                {renderPageButtonsAndEllipses()}
                <PaginationItem>
                  <PaginationNextButton
                    onClick={() => handleChangePage(pageIndex + 1)}
                    disabled={!table.getCanNextPage()}
                  />
                </PaginationItem>
                <PaginationItem className="hidden lg:block">
                  <PaginationEndButton
                    onClick={() => handleChangePage(pageCount - 1)}
                    disabled={pageIndex === pageCount - 1}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
            {children}
          </div>
        </TablePaginationProvider>
      ) : null}
    </div>
  );
}

type TablePaginationContextValue = {
  table: Table<any>;
  onPageChange: (newPage: number) => void;
};

const TablePaginationContext =
  createContext<TablePaginationContextValue | null>(null);

const useTablePaginationContext = (): TablePaginationContextValue => {
  const context = useContext(TablePaginationContext);
  if (!context) {
    throw new Error(
      "TablePaginationPageJump must be used within TablePagination."
    );
  }
  return context;
};

function TablePaginationProvider<T extends Record<string, unknown>>({
  table,
  onPageChange,
  children,
}: PropsWithChildren<{
  table: Table<T>;
  onPageChange: (newPage: number) => void;
}>) {
  return (
    <TablePaginationContext.Provider value={{ table, onPageChange }}>
      {children}
    </TablePaginationContext.Provider>
  );
}

export function TablePaginationPageJump({
  label = "Page",
  variant = "alternate",
}: {
  label?: string;
  variant?: "default" | "alternate";
}): ReactElement | null {
  const { table, onPageChange } = useTablePaginationContext();
  const pageInputId = useId();
  const pageIndex = table.getState().pagination.pageIndex;
  const pageCount = table.getPageCount();
  const currentPage = pageIndex + 1;
  const [pageInput, setPageInput] = useState(currentPage.toString());

  useEffect(() => {
    setPageInput(currentPage.toString());
  }, [currentPage]);

  const handlePageJumpCommit = useCallback(() => {
    if (pageCount < 1) return;

    const parsedPage = Number.parseInt(pageInput.trim(), 10);
    if (!Number.isFinite(parsedPage)) {
      setPageInput(currentPage.toString());
      return;
    }

    const clampedPage = Math.min(Math.max(parsedPage, 1), pageCount);
    setPageInput(clampedPage.toString());

    if (clampedPage === currentPage) return;
    onPageChange(clampedPage - 1);
  }, [currentPage, onPageChange, pageCount, pageInput]);

  return (
    <div className="flex items-center gap-2 px-2 py-1">
      <label
        htmlFor={pageInputId}
        className="text-sm font-medium text-foreground whitespace-nowrap shrink-0"
      >
        {label}
      </label>
      <Input
        id={pageInputId}
        type="number"
        min={1}
        max={pageCount}
        inputMode="numeric"
        size="sm"
        variant={variant}
        className="w-20"
        aria-label="Jump to page"
        value={pageInput}
        onChange={(event) => setPageInput(event.target.value)}
        onBlur={handlePageJumpCommit}
        onKeyDown={(event) => {
          if (event.key !== "Enter") return;
          event.preventDefault();
          handlePageJumpCommit();
        }}
      />
      <span className="text-xs text-foreground-soft whitespace-nowrap">
        of {pageCount}
      </span>
    </div>
  );
}
